import React, { useEffect, useState } from "react";
import {
    AuthenticatedRoute,
    UserAccess
} from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Breadcrumbs, HorizontalLinkGroup } from "components/navigation";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { LinkButton } from "components/buttons";
import { EventListing, EventSchedules, DefectListing, CostListing } from "pages/asset/events";
import { FleetHubOverview } from "pages/fleet";
import { AssetService, FleetService, LocationService } from "services";
import { NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { useSelector, useDispatch } from 'react-redux';
import { SetFiltering } from 'state/actions';
import PropTypes, { } from "prop-types";
import axios from "axios";

export function FleetHub({
    breadcrumbs = []
}) {
    var fleetView = "FMS:View";
    var fleetHubReduxName = "fleetHubRedux";
    var [showCostsTab, setShowCostsTab] = useState(false);
    var routes = [
        { location: "/fleet/overview", title: "Overview" },
        { location: "/fleet/events", title: "Events" },
        { location: "/fleet/schedules", title: "Schedules" },
        { location: "/fleet/defects", title: "Defects" }
    ];

    var [loading, setLoading] = useState(false);
    var [mappedAssetOptions, setMappedAssetOptions] = useState({});
    var [eventOptions, setEventOptions] = useState(null);
    var [eventTypes, setEventTypes] = useState(null);
    var [tachoVisLocationList, setTachoVisLocationList] = useState(null);
    var [selectedLocations, setSelectedLocations] = useState(null);
    var reduxHub = useSelector(state => state.filters[fleetHubReduxName]);
    var [reduxLoaded, setReduxLoaded] = useState(false);
    var dispatch = useDispatch();
    var location = useLocation();
    var navigate = useNavigate();

    useEffect(() => {
        if (!reduxLoaded) {
            setReduxLoaded(true);
            if (reduxHub?.pathname) {
                if (!location.pathname.startsWith(reduxHub.pathname)) {
                    navigate(reduxHub.pathname);
                }
            }
        }
    }, [reduxHub]);

    function tabChanged(title, tabLocation) {
        dispatch(SetFiltering(fleetHubReduxName, {
            pathname: tabLocation,
            title: title
        }));
    }

    useEffect(() => {
        setLoading(true);
        setShowCostsTab(reduxHub?.pathname === "/fleet/costs");
        axios.all([
            AssetService.getAssetOptions(),
            FleetService.getEventOptions(),
            FleetService.getEventTypesForAccount(),
            LocationService.getFormLocationSelectorCustomLocations(['Tacho', 'Vis']),
        ]).then(axios.spread(function (assetOptionsResponse, eventOptionsResponse, eventTypeResponse, tachoVisLocationResponse) {
            if (assetOptionsResponse != null) {
                var ownershipAttributeMappings = assetOptionsResponse.assetOwnerships.map((x) => {
                    return { "name": x.name, "colours": x.colour, "id": x.id, "className": x.style, "icon": x.icon }
                });
                var assetStatusesMappings = assetOptionsResponse.assetStatuses.map((x) => {
                    return { "name": x.name, "colours": x.colour, "id": x.id }
                });

                setMappedAssetOptions(
                    { ownershipAttributeMappings: ownershipAttributeMappings, assetStatusesMappings: assetStatusesMappings, assetTypes: assetOptionsResponse.assetTypes }
                );
                setTachoVisLocationList(tachoVisLocationResponse);
            }
            setEventOptions(eventOptionsResponse);

            var mappedeventTypes = eventTypeResponse.filter(et => !et.hidden);
            setEventTypes(mappedeventTypes);
            setLoading(false);

            if (Array.isArray(mappedeventTypes) && mappedeventTypes.some(et => et.includeCosts)) {
                setShowCostsTab(true);
            } else {
                setShowCostsTab(false);
            }
        }), () => {
            NotificationToaster.show(Intent.DANGER, "Could not load the event data needed. Please refresh the page.");
            setLoading(false);
        });

    }, []);

    var navigationRoutes = showCostsTab
        ? [...routes, { location: "/fleet/costs", title: "Costs" }]
        : routes;

    return (
        <UserAccess
            perform={fleetView}
            yes={() => (
                <div className="row">
                    <Breadcrumbs items={breadcrumbs} />
                    <h1>Fleet Overview</h1>

                    <div className="button-row button-row-stacked-mobile spacer-bottom">
                        <div className="inline-items">
                            <div>
                                <LinkButton
                                    text="Fleet Planner"
                                    intent="primary"
                                    href="/fleet/manage"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="spacer-bottom">
                        <HorizontalLinkGroup
                            links={navigationRoutes}
                            exactRouteMatch={false}
                            onChange={tabChanged}
                        ></HorizontalLinkGroup>
                    </div>

                    <Routes>
                        <Route path="overview" element={<AuthenticatedRoute>
                            <FleetHubOverview
                                locationList={tachoVisLocationList}
                                loading={loading}
                                selectedLocations={selectedLocations}
                                setSelectedLocations={setSelectedLocations}
                            />
                        </AuthenticatedRoute>} />
                        <Route path="events" element={<AuthenticatedRoute>
                            <EventListing
                                pageState="HUB"
                                eventOptions={eventOptions}
                                mappedAssetOptions={mappedAssetOptions}
                                loading={loading}
                                locationList={tachoVisLocationList}
                                eventTypes={eventTypes}
                            />
                        </AuthenticatedRoute>} />
                        <Route path="schedules" element={<AuthenticatedRoute>
                            <EventSchedules
                                pageState="HUB"
                                mappedAssetOptions={mappedAssetOptions}
                                loading={loading}
                                locationList={tachoVisLocationList}
                                eventTypes={eventTypes}
                            />
                        </AuthenticatedRoute>} />
                        <Route path="defects" element={<AuthenticatedRoute>
                            <DefectListing
                                pageState="HUB"
                                eventOptions={eventOptions}
                                mappedAssetOptions={mappedAssetOptions}
                                loading={loading}
                                locationList={tachoVisLocationList}
                                eventTypes={eventTypes}
                            />
                        </AuthenticatedRoute>} />
                        {(showCostsTab) && (
                            <Route path="costs" element={<AuthenticatedRoute>
                                <CostListing
                                    eventOptions={eventOptions}
                                    eventTypes={eventTypes}
                                    locationList={tachoVisLocationList}
                                    pageState="HUB"
                                    mappedAssetOptions={mappedAssetOptions}
                                />
                            </AuthenticatedRoute>} />
                        )}
                        <Route path="" element={<Navigate to="/fleet/overview" replace />} />
                    </Routes>
                </div>
            )}
            no={() => <PageRestricted />}
        />
    );
}

FleetHub.propTypes = {
    breadcrumbs: PropTypes.array,
};
